// Spring menu drawer: accessible open/close controller.
//
// The drawer markup (snippets/spring-menu-drawer.liquid) uses real <button>
// elements for the burger trigger and the close X — no label/checkbox hack,
// and no legacy <navigation-wrapper> (theme.js's NavigationWrapper never
// binds to this drawer). This element owns the whole lifecycle:
//
//   - click on trigger toggles; click on close X or backdrop closes
//   - Escape closes while open
//   - aria-expanded on the trigger is kept in sync
//   - focus moves to the close button once the drawer has opened, and
//     returns to the burger trigger after it closes
//
// Open/close visuals reuse the proven theme.css class machinery (the same
// classes NavigationWrapper applied for the legacy drawer):
//   opening: trigger-open-drawer-animation + is-opening  →  is-open
//   closing: trigger-close-drawer-animation + is-closing →  (none)
// `is-open`/`is-opening`/`is-closing` drive display, the fixed backdrop and
// the body scroll lock; the `trigger-*` classes run the slide/fade keyframes
// (scoped in theme.css to `.navigation.drawer-breakpoint`, hardcoded on the
// nav in the snippet).
class SpringMenuDrawer extends HTMLElement {
  classes = {
    open: "is-open",
    opening: "is-opening",
    closing: "is-closing",
    openAnimation: "trigger-open-drawer-animation",
    closeAnimation: "trigger-close-drawer-animation",
  };

  connectedCallback() {
    this.panel = this.querySelector(".submenu--primary");
    this.toggle = this.querySelector(".navigation__control");
    this.closeButton = this.querySelector(".spring-menu-drawer__close");
    this.backdrop = this.querySelector(".navigation__backdrop");

    if (!this.panel || !this.toggle) return;

    this.eventController = new AbortController();
    const eventOptions = { signal: this.eventController.signal };

    this.toggle.addEventListener(
      "click",
      () => (this.isOpen ? this.close() : this.open()),
      eventOptions,
    );

    this.closeButton?.addEventListener(
      "click",
      () => this.close(),
      eventOptions,
    );

    this.backdrop?.addEventListener(
      "click",
      () => this.close(),
      eventOptions,
    );

    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Escape" && this.isOpen) this.close();
      },
      eventOptions,
    );
  }

  disconnectedCallback() {
    this.eventController?.abort();
  }

  get isOpen() {
    const { open, opening } = this.classes;
    return (
      this.panel.classList.contains(open) ||
      this.panel.classList.contains(opening)
    );
  }

  get targets() {
    return [this.panel, this.backdrop].filter(Boolean);
  }

  // Wait for every CSS animation currently running on the targets (same
  // technique as the legacy NavigationWrapper). Resolves immediately when
  // there are no animations (e.g. reduced motion / `do-anim` off).
  animationsSettled(targets) {
    const animations = targets.flatMap((el) =>
      el.getAnimations({ subtree: true }),
    );
    return Promise.all(animations.map((animation) => animation.finished));
  }

  open() {
    const { open, opening, closing, openAnimation, closeAnimation } =
      this.classes;
    const targets = this.targets;

    targets.forEach((target) => {
      target.classList.remove(closeAnimation, closing);
      target.classList.add(openAnimation, opening);
    });

    this.toggle.setAttribute("aria-expanded", "true");

    this.animationsSettled(targets)
      .then(() => {
        targets.forEach((target) => {
          target.classList.remove(openAnimation, opening);
          target.classList.add(open);
        });

        // Move focus into the drawer once it is fully open.
        this.closeButton?.focus();
      })
      .catch(() => {
        // Opening was interrupted by a close; only clear open-state classes.
        targets.forEach((target) => {
          target.classList.remove(openAnimation, opening, open);
        });
      });
  }

  close() {
    const { open, opening, closing, openAnimation, closeAnimation } =
      this.classes;
    const targets = this.targets;

    targets.forEach((target) => {
      target.classList.remove(openAnimation, opening);
      target.classList.add(closeAnimation, closing);
    });

    this.toggle.setAttribute("aria-expanded", "false");

    const cleanup = () => {
      targets.forEach((target) => {
        target.classList.remove(closeAnimation, closing, open);
      });
    };

    this.animationsSettled(targets)
      .then(() => {
        cleanup();
        // Return focus to the burger trigger (a real button).
        this.toggle.focus();
      })
      .catch(cleanup);
  }
}

if (!customElements.get("spring-menu-drawer")) {
  customElements.define("spring-menu-drawer", SpringMenuDrawer);
}
